import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicFeedbackAPI, publicSettingsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import SignatureCanvas from 'react-signature-canvas';
import { CheckCircle, AlertCircle, Loader2, Eraser } from 'lucide-react';
import { toast } from 'sonner';

const ChefFeedbackForm = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const sigCanvas = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [template, setTemplate] = useState(null);

  const [responses, setResponses] = useState({});
  const [comments, setComments] = useState({});
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');
  const [trainingFeedback, setTrainingFeedback] = useState('');
  const [generalComments, setGeneralComments] = useState('');
  const [chefName, setChefName] = useState('');
  const [signatureType, setSignatureType] = useState('drawn');
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetchForm();
    fetchLogo();
  }, [token]);

  const fetchLogo = async () => {
    try {
      const res = await publicSettingsAPI.get();
      if (res.data?.logo_url) setLogoUrl(res.data.logo_url);
    } catch (e) { /* ignore */ }
  };

  const fetchForm = async () => {
    try {
      const res = await publicFeedbackAPI.getByToken(token);
      setForm(res.data.form);
      setTemplate(res.data.template);
      setChefName(res.data.form.chef_name || '');
      // Check if template exists
      if (!res.data.template) {
        setError('The assessment template for this form is no longer available. Please contact the administrator.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Unable to load form';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const setRating = (elementId, value) => {
    setResponses(prev => ({ ...prev, [elementId]: value }));
  };

  const setComment = (elementId, value) => {
    setComments(prev => ({ ...prev, [elementId]: value }));
  };

  const setTextResponse = (elementId, value) => {
    setResponses(prev => ({ ...prev, [elementId]: value }));
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredElements = template.elements.filter(e => e.required && e.type !== 'section_header');
    const missing = requiredElements.filter(e => !responses[e.id]);
    if (missing.length > 0) {
      toast.error(`Please complete all required fields (${missing.length} remaining)`);
      return;
    }
    if (!chefName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    let signature = '';
    if (signatureType === 'drawn') {
      if (sigCanvas.current?.isEmpty()) {
        toast.error('Please provide your signature');
        return;
      }
      signature = sigCanvas.current.toDataURL('image/png');
    } else {
      signature = chefName;
    }

    // Merge comments into responses
    const mergedResponses = { ...responses };
    Object.keys(comments).forEach(key => {
      mergedResponses[`${key}_comment`] = comments[key];
    });

    setSubmitting(true);
    try {
      await publicFeedbackAPI.submit(token, {
        responses: mergedResponses,
        strengths,
        weaknesses,
        training_feedback: trainingFeedback,
        general_comments: generalComments,
        chef_name: chefName,
        chef_signature: signature,
        signature_type: signatureType,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Unable to Load Form</h2>
            <p className="text-gray-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Thank You!</h2>
            <p className="text-gray-500 mb-2">Your feedback has been submitted successfully.</p>
            <p className="text-sm text-gray-400">
              Student: {form.student_name} ({form.student_number})
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1 text-center">
                <h1 data-testid="feedback-form-title" className="text-2xl font-bold text-[#1e3a5f]">
                  {template.name}
                </h1>
                {template.description && (
                  <p className="text-gray-500 mt-1">{template.description}</p>
                )}
              </div>
              {logoUrl && (
                <img src={logoUrl} alt="School Logo" className="h-14 w-auto object-contain ml-4 flex-shrink-0" />
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg text-sm">
              <div>
                <p className="text-gray-400 text-xs">Student</p>
                <p className="font-medium text-gray-700">{form.student_name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Student #</p>
                <p className="font-medium text-gray-700">{form.student_number}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Restaurant</p>
                <p className="font-medium text-gray-700">{form.restaurant_name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Assessment</p>
                <p className="font-medium text-gray-700">#{form.assessment_number}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Intro Text */}
        {template.intro_text && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 leading-relaxed">{template.intro_text}</p>
            </CardContent>
          </Card>
        )}

        {/* Scale Legend */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold text-[#1e3a5f] mb-3 text-sm">Assessment Scale</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8 text-sm">
              {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(score => (
                <div key={score} className="flex items-start gap-2">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                    score >= 7 ? 'bg-emerald-100 text-emerald-700' :
                    score >= 5 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {score}
                  </span>
                  <span className="text-gray-600 leading-snug pt-0.5">{template.assessment_scale?.[score.toString()] || ''}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Assessment Elements */}
        <Card>
          <CardContent className="pt-6 space-y-6">
            {template.elements.map((element) => (
              <div key={element.id} data-testid={`element-${element.id}`}>
                {element.type === 'section_header' ? (
                  <h3 className="text-lg font-semibold text-[#1e3a5f] border-b border-gray-200 pb-2 mt-4">
                    {element.label}
                  </h3>
                ) : element.type === 'rating' ? (
                  <div className="p-4 border rounded-lg bg-white">
                    <div className="mb-2">
                      <p className="font-medium text-gray-800">
                        {element.label}
                        {element.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      {element.module && (
                        <span className="text-xs text-[#bc953f]">({element.module})</span>
                      )}
                    </div>
                    {element.description && (
                      <p className="text-sm text-gray-500 mb-3">{element.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
                        <button
                          key={score}
                          type="button"
                          data-testid={`rating-${element.id}-${score}`}
                          onClick={() => setRating(element.id, score)}
                          className={`w-9 h-9 rounded-full text-sm font-medium transition-all ${
                            responses[element.id] === score
                              ? score >= 7
                                ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                                : score >= 5
                                ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                                : 'bg-red-500 text-white ring-2 ring-red-300'
                              : 'border-2 border-gray-200 hover:border-gray-400 text-gray-600'
                          }`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                    <Input
                      placeholder="Additional comments (optional)"
                      value={comments[element.id] || ''}
                      onChange={(e) => setComment(element.id, e.target.value)}
                      className="text-sm"
                    />
                  </div>
                ) : element.type === 'textarea' ? (
                  <div>
                    <Label className="font-medium">
                      {element.label}
                      {element.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {element.description && (
                      <p className="text-sm text-gray-500 mb-2">{element.description}</p>
                    )}
                    <Textarea
                      rows={4}
                      value={responses[element.id] || ''}
                      onChange={(e) => setTextResponse(element.id, e.target.value)}
                      placeholder="Enter your response..."
                    />
                  </div>
                ) : (
                  <div>
                    <Label className="font-medium">
                      {element.label}
                      {element.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {element.description && (
                      <p className="text-sm text-gray-500 mb-2">{element.description}</p>
                    )}
                    <Input
                      value={responses[element.id] || ''}
                      onChange={(e) => setTextResponse(element.id, e.target.value)}
                      placeholder="Enter your response..."
                    />
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Standard Sections */}
        <Card>
          <CardContent className="pt-6 space-y-5">
            <h3 className="font-semibold text-[#1e3a5f] border-b pb-2">Additional Feedback</h3>
            <div>
              <Label>What strengths did the student demonstrate? *</Label>
              <Textarea
                data-testid="strengths-input"
                rows={3}
                value={strengths}
                onChange={(e) => setStrengths(e.target.value)}
                placeholder="Describe the student's strengths..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>What weaknesses did the student demonstrate? *</Label>
              <Textarea
                data-testid="weaknesses-input"
                rows={3}
                value={weaknesses}
                onChange={(e) => setWeaknesses(e.target.value)}
                placeholder="Describe areas for improvement..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>How would you rate the standard of ICA training?</Label>
              <Textarea
                rows={3}
                value={trainingFeedback}
                onChange={(e) => setTrainingFeedback(e.target.value)}
                placeholder="Your feedback on the training program..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>General comments</Label>
              <Textarea
                rows={4}
                value={generalComments}
                onChange={(e) => setGeneralComments(e.target.value)}
                placeholder="Any additional comments..."
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Signature */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-semibold text-[#1e3a5f] border-b pb-2">Signature</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Chef / Mentor Name *</Label>
                <Input
                  data-testid="chef-name-input"
                  value={chefName}
                  onChange={(e) => setChefName(e.target.value)}
                  placeholder="Enter your full name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={new Date().toISOString().split('T')[0]}
                  readOnly
                  className="mt-1 bg-gray-50"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center gap-4 mb-2">
                <Label>Signature *</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSignatureType('drawn')}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      signatureType === 'drawn'
                        ? 'bg-[#1e3a5f] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Draw
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignatureType('typed')}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      signatureType === 'typed'
                        ? 'bg-[#1e3a5f] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Type
                  </button>
                </div>
              </div>

              {signatureType === 'drawn' ? (
                <div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        width: 600,
                        height: 150,
                        className: 'w-full',
                        'data-testid': 'signature-canvas',
                      }}
                      penColor="#1e3a5f"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="mt-2 text-gray-500"
                  >
                    <Eraser className="w-3.5 h-3.5 mr-1" />
                    Clear Signature
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white text-center">
                  <p className="text-2xl italic text-[#1e3a5f]" style={{ fontFamily: 'cursive' }}>
                    {chefName || 'Your name will appear here'}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end pb-8">
          <Button
            data-testid="submit-feedback-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#1e3a5f] hover:bg-[#152a47] text-white px-8 py-3 text-base"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChefFeedbackForm;
